"use client"

import classes from "./DriverRegister.module.scss"
import { useState, useContext } from "react"
import { useNavigate, Link } from "react-router-dom"
import * as Yup from "yup"
import { Formik, Field } from "formik"
import { APIContext } from "../../../connector";
import { driverApi, getErrorMessage } from "../../../api"
import { useToast } from "../../../hooks/useToast"
import backgroundCurveImg from "../../../static/images/curve.svg"
import FormCard from "../../../components/FormCard"
import RoundedEdgeButton from "../../../components/RoundedEdgeButton"

const Register = () => {
  const navigate = useNavigate()
  const { openToast } = useToast();
  const { userData, setUserData, switchProfile } = useContext(APIContext);

  // tracking file sizes one by one
  const [fileSizes, setFileSizes] = useState({
    profile_picture: 0,
    license: 0,
    car_pictures: 0,
    card_matric: 0,
    insurance: 0,
    road_tax: 0,
  })

  // kept as a constant, it will refresh every re-render
  const totalFileSize = Object.values(fileSizes).reduce((sum, size) => sum + size, 0)

  // Required file fields
  const requiredFiles = ["profile_picture", "license", "car_pictures", "card_matric", "insurance", "road_tax"]

  const [selectedFiles, setSelectedFiles] = useState({
    profile_picture: false,
    license: false,
    car_pictures: false,
    card_matric: false,
    insurance: false,
    road_tax: false,
  })

  const allFilesSelected = () => {
    return requiredFiles.every((file) => selectedFiles[file])
  }

  const getMissingFiles = () => {
    return requiredFiles.filter((file) => !selectedFiles[file])
  }

  const handleFileChange = (fieldName, file) => {
    if (file) {
      const fileSize = file.size / 1024 / 1024 //converting to MB

      // first we update file sizes object with new size for this field
      setFileSizes((prev) => ({
        ...prev,
        [fieldName]: fileSize,
      }))

      // here we update selected files status
      setSelectedFiles((prev) => ({
        ...prev,
        [fieldName]: true,
      }))

      return fileSize
    }
    return 0
  }

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    user_status: Yup.string().required("Please select your status"),
    car_model: Yup.string().required("Car model is required"),
    car_color: Yup.string().required("Car color is required"),
    car_plate_number: Yup.string().required("Number plate is required"),
  })

  const submitDriverInfo = async (values, { setSubmitting, setErrors }) => {
    // Check if all files are selected
    if (!allFilesSelected()) {
      const missingFiles = getMissingFiles()
      const missingFileLabels = missingFiles.map((fileId) => {
        const fileObj = fileFields.find((item) => item.id === fileId)
        return fileObj ? fileObj.label : fileId
      })

      openToast(`Please upload all required files. Missing: ${missingFileLabels.join(", ")}`, 5000, "danger")
      setSubmitting(false)
      return
    }

    // Check file size limit
    if (totalFileSize > 10) {
      openToast(`Total file size is ${totalFileSize.toFixed(2)} MB. Max allowed is 10 MB`, 5000, "danger");
      setSubmitting(false)
      return
    }

    const formData = new FormData()

    Object.keys(values).forEach((name) => {
      formData.append(name, values[name])
    })

    try {
      const data = await driverApi.submitDriverInfo(formData);
      setUserData(data);
      // user will be automatically redirected to waiting page until approved
      // this is handled by the SharedRoute component
    } catch (error) {
      console.error("Driver registration error:", error);
      const message = getErrorMessage(error);
      openToast(message || "An error occurred. Please try again.", 5000, "danger");
    } finally {
      setSubmitting(false)
    }
  }

  //upload fields config, can customize and add more fields
  const fileFields = [
    { id: "profile_picture", label: "Profile Photo" },
    { id: "license", label: "Driver License" },
    { id: "car_pictures", label: "Car Photos" },
    { id: "card_matric", label: "Card Matric" },
    { id: "insurance", label: "Insurance" },
    { id: "road_tax", label: "Road Tax" },
  ]

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        name: userData?.name,
        car_plate_number: "",
        user_status: "",
        car_model: "",
        car_color: "",
      }}
      validationSchema={validationSchema}
      onSubmit={submitDriverInfo}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => (
        <>
          <div className={classes.contianer}>
            <div className={classes.backgroundCurve}>
              <img src={backgroundCurveImg || "/placeholder.svg"} alt="curve" />
            </div>
            <div className={classes.innerCardContent}>
              <h1 className={classes.title}>Driver Registration</h1>
              <Link className={classes.link} onClick={() => {
                switchProfile("CLIENT")
                  .then(() => {
                    navigate("/");
                  })
              }}>
                Proceed as passenger instead? Click here
              </Link>

              <form noValidate onSubmit={handleSubmit}>
                {/* Name */}
                <FormCard className={classes.formCard}>
                  <div className={classes.inputRow}>
                    <p className={classes.fieldTitle}>
                      Name <span className={classes.required}>*</span>
                    </p>
                    <input
                      className={`${classes.inputField} ${errors.name && touched.name ? classes.errorInput : ""}`}
                      type="text"
                      name="name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name}
                      id="name"
                      disabled={!!userData?.name}
                    />
                  </div>
                  {touched.name && errors.name && <div className={classes.errorText}>{errors.name && touched.name ? errors.name : ""}</div>}
                </FormCard>

                {/* Status */}
                <FormCard className={classes.formCard}>
                  <div className={classes.inputRow}>
                    <p className={classes.fieldTitle}>
                      Status <span className={classes.required}>*</span>
                    </p>
                    <ul
                      className={`${classes.radioGroup} ${errors.user_status && touched.user_status ? classes.errorBorder : ""}`}
                    >
                      <li>
                        <Field type="radio" id="user" name="user_status" value="STUDENT" />
                        <label htmlFor="user">Student</label>
                      </li>
                      <li>
                        <Field type="radio" id="other" name="user_status" value="STAFF" />
                        <label htmlFor="other">Staff</label>
                      </li>
                    </ul>
                  </div>
                  {errors.user_status && touched.user_status && <div className={classes.errorText}>
                    {errors.user_status && touched.user_status ? errors.user_status : ""}
                  </div>}
                </FormCard>

                {/* Car Model */}
                <FormCard className={classes.formCard}>
                  <div className={classes.inputRow}>
                    <p className={classes.fieldTitle}>
                      Car Model <span className={classes.required}>*</span>
                    </p>
                    <input
                      className={`${classes.inputField} ${errors.car_model && touched.car_model ? classes.errorInput : ""}`}
                      type="text"
                      name="car_model"
                      placeholder="Perodua Myvi"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.car_model}
                      id="car_model"
                    />
                  </div>
                  {errors.car_model && touched.car_model && <div className={classes.errorText}>
                    {errors.car_model && touched.car_model ? errors.car_model : ""}
                  </div>}
                </FormCard>

                {/* Car Colour */}
                <FormCard className={classes.formCard}>
                  <div className={classes.inputRow}>
                    <p className={classes.fieldTitle}>
                      Car Colour <span className={classes.required}>*</span>
                    </p>
                    <input
                      className={`${classes.inputField} ${errors.car_color && touched.car_color ? classes.errorInput : ""}`}
                      type="text"
                      name="car_color"
                      placeholder="White"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.car_color}
                      id="car_color"
                    />
                  </div>
                  {errors.car_color && touched.car_color && <div className={classes.errorText}>
                    {errors.car_color && touched.car_color ? errors.car_color : ""}
                  </div>}
                </FormCard>

                {/* Number Plate */}
                <FormCard className={classes.formCard}>
                  <div className={classes.inputRow}>
                    <p className={classes.fieldTitle}>
                      Number Plate <span className={classes.required}>*</span>
                    </p>
                    <input
                      className={`${classes.inputField} ${errors.car_plate_number && touched.car_plate_number ? classes.errorInput : ""}`}
                      type="text"
                      name="car_plate_number"
                      placeholder="HWE 1234"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.car_plate_number}
                      id="car_plate_number"
                    />
                  </div>
                  {errors.car_plate_number && touched.car_plate_number && <div className={classes.errorText}>
                    {errors.car_plate_number && touched.car_plate_number ? errors.car_plate_number : ""}
                  </div>}
                </FormCard>

                {/* Attachments */}
                <FormCard className={classes.attachmentsCard}>
                  {/* File upload fields */}
                  {fileFields.map((field) => (
                    <div key={field.id}>
                      <div
                        className={`${classes.attachmentButton} ${selectedFiles[field.id] ? classes.fileSelected : ""}`}
                      >
                        <input
                          id={field.id}
                          type="file"
                          accept="image/png, image/jpeg"
                          onChange={(e) => {
                            if (e.currentTarget.files && e.currentTarget.files[0]) {
                              const file = e.currentTarget.files[0]
                              handleFileChange(field.id, file)
                              setFieldValue(field.id, file)
                            }
                          }}
                        />
                        <label htmlFor={field.id}>
                          <i className={`fa-solid ${selectedFiles[field.id] ? "fa-check" : "fa-upload"}`}></i>
                          {!selectedFiles[field.id] && field.label}
                          {selectedFiles[field.id] && `${field.label} - Saved`}
                        </label>
                      </div>
                    </div>
                  ))}
                </FormCard>

                {/* File upload progress */}
                <div className={classes.uploadProgress}>
                  <div className={classes.progressText}>
                    <span>Total file size: {totalFileSize.toFixed(2)} MB / 10 MB</span>
                  </div>
                </div>

                {/* Submit Button */}
                <RoundedEdgeButton
                  className={`${classes.submitButton} ${!allFilesSelected() || Object.keys(errors).length > 0 ? classes.disabledButton : ""}`}
                  type="submit"
                  disabled={!allFilesSelected() || Object.keys(errors).length > 0 || isSubmitting}
                >
                  SUBMIT
                </RoundedEdgeButton>
              </form>
            </div>
          </div>
        </>
      )}
    </Formik>
  )
}

export default Register
